/**
 * Thin fetch wrapper over the admin API.
 *
 * Same-origin, so the auth cookie rides along with `credentials: 'include'` and there is no token
 * to store in the browser. A 401 anywhere means the session is gone (expired, suspended, password
 * changed elsewhere) and is turned into a typed error the shell can react to by showing the login
 * screen — rather than every page having to handle it.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly reasonCode: string | null,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  get isUnauthorized() {
    return this.status === 401
  }
}

type Query = Record<string, string | number | boolean | null | undefined>

function withQuery(path: string, query?: Query) {
  if (!query) return path
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v !== null && v !== undefined && v !== '') params.set(k, String(v))
  }
  const qs = params.toString()
  return qs ? `${path}?${qs}` : path
}

async function request<T>(method: string, path: string, body?: unknown, query?: Query): Promise<T> {
  const res = await fetch(withQuery(path, query), {
    method,
    credentials: 'include',
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  if (res.status === 204) return undefined as T

  const text = await res.text()
  const payload = text ? safeParse(text) : null

  if (!res.ok) {
    // The API answers refusals as RFC 9457 problem documents with a stable reasonCode, which is
    // what the UI keys its messages off — never the prose.
    const detail =
      (payload as { detail?: string; title?: string } | null)?.detail ??
      (payload as { title?: string } | null)?.title ??
      res.statusText
    const reasonCode = (payload as { reasonCode?: string } | null)?.reasonCode ?? null
    throw new ApiError(res.status, reasonCode, detail)
  }

  return payload as T
}

function safeParse(text: string) {
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

export const api = {
  get: <T>(path: string, query?: Query) => request<T>('GET', path, undefined, query),
  post: <T>(path: string, body?: unknown, query?: Query) => request<T>('POST', path, body, query),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
}

// ---------------------------------------------------------------- types

export type AdminRole = 'Viewer' | 'Operator' | 'Admin' | 'Owner'

export const ROLE_RANK: Record<AdminRole, number> = {
  Viewer: 0,
  Operator: 1,
  Admin: 2,
  Owner: 3,
}

export interface AdminUser {
  id: string
  email: string
  displayName: string
  role: AdminRole
  isActive: boolean
  mustChangePassword: boolean
  totpEnabled: boolean
  lastLoginAt: string | null
  passwordChangedAt: string | null
  createdAt: string
}

export type HealthState = 'Unknown' | 'Healthy' | 'Degraded' | 'Dark'

export interface DeviceGroupRef {
  id: string
  name: string
  colour: string | null
}

export interface Device {
  id: string
  deviceId: string
  name: string
  label: string | null
  msisdn: string | null
  operator: string | null
  approvalState: string
  healthState: HealthState
  darkReason: string | null
  isEnabled: boolean
  isConnected: boolean
  rssi: number | null
  balanceAmount: number | null
  balanceCurrency: string | null
  lastHeartbeatAt: string | null
  firmwareVersion: string | null
  weight: number
  maxInFlight: number
  rateLimitPerMinute: number
  dailyCap: number
  dispatchCounter: number
  inFlight: number
  groups: DeviceGroupRef[]
}

export interface Enrolment {
  id: string
  deviceId: string
  imei: string | null
  iccid: string | null
  modemModel: string | null
  firmwareVersion: string | null
  firstSeenAt: string
  lastSeenAt: string
  attempts: number
  remoteIp: string | null
}

export interface DeviceGroup {
  id: string
  name: string
  description: string | null
  colour: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string
  members: { simClientId: string; name: string }[]
  sources: { id: string; name: string }[]
}

export interface SourceSummary {
  id: string
  name: string
  slug: string
  isActive: boolean
  inboundMode: string
  rateLimitPerMinute: number
  dailyQuota: number | null
  activeKeys: number
  sentToday: number
  groups: DeviceGroupRef[]
}

export interface ApiKeyRow {
  id: string
  name: string
  keyPrefix: string
  scopes: string[]
  isTestKey: boolean
  lastUsedAt: string | null
  lastUsedIp: string | null
  expiresAt: string | null
  revokedAt: string | null
  createdAt: string
}

export interface SourceDetail {
  id: string
  name: string
  slug: string
  isActive: boolean
  webhookUrl: string | null
  webhookEvents: string[]
  inboundMode: string
  inboundKeywords: string[]
  rateLimitPerMinute: number
  dailyQuota: number | null
  maxPartsPerMessage: number
  defaultValiditySeconds: number
  retryOnUnknown: boolean
  allowClientHint: boolean
  allowInternational: boolean
  correlationTtlDays: number
  honourOptOut: boolean
  hasWebhookSecret: boolean
  createdAt: string
  apiKeys: ApiKeyRow[]
  groupIds: string[]
  autoReplyRules: { id: string; name: string; matchType: string; matchValue: string | null; isActive: boolean }[]
}

export interface AutoReplyRule {
  id: string
  name: string
  sourceId: string | null
  clientId: string | null
  matchType: string
  matchValue: string | null
  replyBody: string
  mode: string
  isActive: boolean
  priority: number
  quietHoursStart: number | null
  quietHoursEnd: number | null
  createdAt: string
  sourceName: string | null
  clientName: string | null
  repliedCount: number
}

export interface DashboardFleetRow {
  id: string
  name: string
  msisdn: string | null
  healthState: HealthState
  darkReason: string | null
  darkSince: string | null
  nextProbeAt: string | null
  isConnected: boolean
  isEnabled: boolean
  rssi: number | null
  balanceAmount: number | null
  balanceCurrency: string | null
  balanceExpiresOn: string | null
  sentToday: number
  failedLastHour: number
  lastHeartbeatAt: string | null
}

export interface Dashboard {
  fleet: DashboardFleetRow[]
  counts: {
    online: number
    dark: number
    total: number
    pendingEnrolments: number
    queueDepth: number
    parked: number
    sentToday: number
    openAlerts: number
  }
  throughput: { hour: string; count: number }[]
  recentFailures: {
    id: string
    msisdn: string
    reasonCode: string | null
    reasonDetail: string | null
    failedAt: string | null
  }[]
}

export interface MessageRow {
  deliveryId: string
  messageId: string
  msisdn: string
  status: string
  reasonCode: string | null
  attemptCount: number
  createdAt: string
  submittedAt: string | null
  body: string | null
  sourceName: string | null
  clientName: string | null
}

export interface InboundRow {
  id: string
  fromMsisdn: string
  toMsisdn: string | null
  body: string | null
  receivedAt: string
  routingReason: string | null
  clientName: string | null
  sourceName: string | null
  autoReplied: boolean
  forwardStatus: string | null
}

export interface AlertRow {
  id: string
  type: string
  severity: 'Info' | 'Warning' | 'Critical'
  state: 'Open' | 'Acknowledged' | 'Resolved'
  title: string
  detail: string | null
  entityType: string | null
  entityId: string | null
  occurrences: number
  openedAt: string
  lastAt: string
  ackedAt: string | null
  resolvedAt: string | null
}

export interface OperatorProfile {
  id: string
  name: string
  mccMnc: string[]
  msisdnPrefixes: string[]
  balanceUssd: string | null
  balanceRegex: string | null
  expiryRegex: string | null
  expiryDateFormat: string | null
  currency: string
  ussdTimeoutSeconds: number
  isVerified: boolean
  costPerSms: number | null
  notes: string | null
}
